import Vue from 'vue';
import MessageRouter from "@/routers/message";
import EventService from "@/services/EventService";
import {CHAT_SUBTYPES} from "@/components/Config";

export const namespaced = true;

export const HISTORY_PAGE_SIZE = 30;

export const MSG_TYPES = {
    MESSAGE: 'chat.message',
    JOIN: 'chat.join',
    LEAVE: 'chat.leave',
    PRESENCE: 'chat.presence',
    HISTORY: 'chat.history',
    READ: 'chat.read',
    ONLINE: 'event.online',
    BAN: 'moderator.ban',
    DELETE: 'moderator.delete',
    DELETE_ALL: 'moderator.delete.all',

    ERROR_CHAT: 'chat.error',
    ERROR_MODERATOR: 'moderator.error',
};


/** Convert a Django MessageDetailSerializer object to WebSocket ChatMessage format. */
const convertThreadToMessage = (thread) => {
    return {
        "type":"chat.message",
        "message": thread.message,
        "created_at": thread.created_at,
        "room": thread.room,
        "message_id": thread.id,
        "sender_id": thread.sender_id,
        "sender_name": thread.sender ? thread.sender.full_name : '',
        "sender_org": thread.sender ? thread.sender.organisation_name : '',
    }    
}

/** Convert an *inbound* chat message to a barebones thread equivalent */
const convertMessageToThread = (msg, me) => {
    return {
        type: "chat.message",
        message: msg.message,
        created_at: msg.created_at,
        room: msg.room,
        id: msg.message_id,
        sender_id: msg.sender_id,
        sender: { 
            id: msg.sender_id,
            full_name: msg.sender_name,
            organisation_name: msg.organisation_name,
        },
        attendee_id: me.id,
        attendee: {
            id: me.id,
            full_name: me.full_name,
            organisation_name: me.organisation_name,
        }
    }
}

// NOTIFICATIONS 
const audioNotification = new Audio("/audio/ping.mp3");
audioNotification.volume = 0.5;

const MUTE_ALERTS_FLAG = 'MUTE_ALERTS';
const muteAlerts = (isMuted) => {
    if (typeof isMuted !== 'undefined') {
        localStorage.setItem(MUTE_ALERTS_FLAG, isMuted);
    }
    let curMuted = localStorage.getItem(MUTE_ALERTS_FLAG);
    if (typeof curMuted === 'undefined') { return false; }
    return curMuted;
}




/** UNDERLYING SOCKET, QUEUE AND COUNTERS
 * Adjacent to Vuex for convenience, but doesn't need to be in the store.
 */
var _socket = null;
var _queue = []; // If the socket isn't ready, queue the messages and send them onopen
var _attempts = 1;
var _MAX = 5;


/** Wrapper for WebSocket.send() that handles socket outages. */
const sendOrQueue = (msg) => {
    if (_socket && _socket.readyState == WebSocket.OPEN) {
        _socket.send(msg);
    } else {
        _queue.push(msg);
    }
}

export const state = {
    // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
    socketState: null, // CONNECTING, OPEN, CLOSING, CLOSED
    error: null,
    
    latest: [],  // Ordered list of the single most recent message in each room
    threadCache: {}, // message objects don't include all the appointment/meeting/1:1 context so cache that critical data here
    
    messages: [], // All the chat.message objects received
    ready: [], // List of rooms that have received chat.join objects

    online: [], // List of attendee_ids online right now

    errorByRoom: {},
    presencesByRoom: {}, //  [room] = [{attendee_id, name, organisation_name, timestamp}, ...]
    firstByRoom: {}, // [room] = msg_id - Record the first message sent, so that we can stop unnecessary history calls. Built as we go.
    myPointersByRoom: {},  // [room] = msg_id - id of the last message read in each room
    unreadByRoom: {}, // [room] = last_pointer_id or 0 : Recalculated when messages/pointers update
    
    draftByRoom: {}, // [room] = string - so that draft contents are not lost if viewer switches accidentally

    roomsByAttendeeId: {}, // {attendeeId : [ROOM, ROOM], ...}

    isUnpacking: false, // Flag set when a compressed set of messages is being processed.
    pageTitle: '', // Cache of page title, so that we can override with unread message count after loading
    
    // ========================
    byId: {}, // Obsolete?
    statusesByRoom: {}, // Replace with getter?
    pointers: []
};

var bgSocket = null;

export const mutations = {
    // LATEST
    SET_LATEST(state, items) {
        // Don't overwrite any pending threads the attendee is currently writing.
        // Pending threads don't have a message id
        const pendings = state.latest.filter(thread => !thread.message_id);
        state.latest = pendings.concat(items);
    },
    PREPEND_NEW(state, item) { // Add a new discussion to the top of the list
        // Only prepend if the room doesn't already exist
        const index = state.latest.findIndex(msg => msg.room === item.room);
        if (index === -1) { 
            state.latest.unshift(item);
        }
    },
    REMOVE_FROM_LATEST(state, item) {
        const index = state.latest.findIndex(msg => msg.room === item.room);
        if (index !== -1) { 
            state.latest.splice(index, 1); 
        }
    },

    // THREADCACHE
    SET_THREAD_CACHE(state, items) {
        items.forEach(item => {
            Vue.set(state.threadCache, item.room, item);
        });
    },
    REMOVE_FROM_THREAD_CACHE(state, item) {
        if (item.room in state.threadCache) {
            console.log("REMOVE_FROM_THREAD_CACHE:", item);
            Vue.delete(state.threadCache, item.room);
        }
    },

    // MESSAGES
    INSERT_MESSAGE(state, msg) {
        // Add the message at the correct position in the list
        let position = 0;
        for (let i=0; i < state.messages.length; i++) {
            let curMsg = state.messages[i];
            if (curMsg.message_id > msg.message_id) { break; }
            position++;
        }
        // console.log("Inserting message at position:", position);
        state.messages.splice(position, 0, msg);
    },
    REMOVE_MESSAGE_BY_ID(state, messageId) {
        const index = state.messages.findIndex(msg => msg.message_id === messageId);
        if (index !== -1) { state.messages.splice(index, 1); }
    },
    REMOVE_MESSAGES_BY_ROOM(state, room) {
        // Wipe all the messages in the specified room
        state.messages = state.messages.filter(msg => msg.room !=  room);
    },

    // ONLINE 
    ADD_ONLINE(state, attendee_ids) {
        attendee_ids.forEach(attendee_id => {
            if (!state.online.includes(attendee_id)) {
                state.online.push(attendee_id);
            }    
        });
    },
    REMOVE_ONLINE(state, attendee_ids) {
        attendee_ids.forEach(attendee_id => {
            const index = state.online.findIndex(id => id === attendee_id);
            if (index !== -1) { state.online.splice(index, 1); }
        });
    },

    // ERROR BY ROOM
    SET_ERROR_BY_ROOM(state, msg) {
        if (Object.prototype.hasOwnProperty.call(msg, 'room')) {
            Vue.set(state.errorByRoom, msg.room, msg);
        }
    },

    // PRESENCE BY ROOM
    ADD_PRESENCES(state, {room, messages}) {
        if (!(room in state.presencesByRoom)) {
            Vue.set(state.presencesByRoom, room, []);
        }
        const presences = state.presencesByRoom[room];

        messages.forEach(msg => {
            let index = presences.findIndex(presence => presence.attendee_id == msg.attendee_id);
            if (index !== -1) {
                presences[index] = msg;
            } else {
                presences.push(msg);
            }
        });
        
        Vue.set(state.presencesByRoom, room, presences);
    },
    REMOVE_PRESENCE(state, {room, attendee_id}) {
        // Remove the attendee from the room. Used when banning people
        const presences = state.presencesByRoom[room];
        if (presences) {
            let index = presences.findIndex(presence => presence.attendee_id == attendee_id);
            if (index !== -1) {
                presences.splice(index, 1);
                Vue.set(state.presencesByRoom, room, presences);
            }
        }
    },

    // Only set firstByRoom if we're _sure_ there's nothing older.
    SET_FIRST_BY_ROOM(state, {room, message_id}) {
        Vue.set(state.firstByRoom, room, message_id);
    },


    // Message read by room - Replaces the pointers(?)
    SET_MY_POINTERS_BY_ROOM(state, {room, message_id}) {
        if (state.myPointersByRoom[room]) {
            if (state.myPointersByRoom[room] > message_id) {
                // We've already got a more recent entry, so ignore this one
                return;
            }
        }
        Vue.set(state.myPointersByRoom, room, message_id);
    },

    UPDATE_UNREAD_BY_ROOM(state) {
        // console.log("UPDATE_UNREAD_BY_ROOM");
        state.latest.forEach(msg => {
            if (msg.room in state.myPointersByRoom) {
                if (msg.message_id > state.myPointersByRoom[msg.room]) {
                    Vue.set(state.unreadByRoom, msg.room, state.myPointersByRoom[msg.room]);
                    // unread[msg.room] = state.myPointersByRoom[msg.room];
                } else {
                    Vue.delete(state.unreadByRoom, msg.room);
                }
            } else {
                // No pointer, so it must be unread
                // Set message_id to 0 as oldest possible value
                // unread[msg.room] = 0;
                if (msg.message_id) {
                    Vue.set(state.unreadByRoom, msg.room, 0);
                }
            }
        });

        // console.log("UPDATE_UNREAD_BY_ROOM:UNREAD:", state.unreadByRoom);
        // Remove rooms that only exist in unreadByRoom, to prevent over-counts
        //let latestRooms = Object.keys(state.latest);
        let latestRooms = state.latest.map(msg => msg.room);
        let unreadRooms = Object.keys(state.unreadByRoom);
        // console.log("UPDATE_UNREAD_BY_ROOM:LATEST", latestRooms);
        // console.log("UPDATE_UNREAD_BY_ROOM:UNREAD", unreadRooms);
        unreadRooms.forEach(room => {
            if (! latestRooms.includes(room)) {
                console.log("UPDATE_UNREAD_BY_ROOM:REMOVING:", room);
                Vue.delete(state.unreadByRoom, room);
            }
        });
    },

    SET_DRAFT_BY_ROOM(state, {room, draft}) {
        Vue.set(state.draftByRoom, room, draft);
    },

    SET_ROOM_BY_ATTENDEE(state, {room, attendee_id}) {
        if (attendee_id in state.roomsByAttendeeId) {
            const index = state.roomsByAttendeeId[attendee_id].indexOf(room);
            if (index == -1) {
                state.roomsByAttendeeId[attendee_id].push(room);
            }
        } else {
            Vue.set(state.roomsByAttendeeId, attendee_id, [room]);
        }
    },

    SET_UNPACKING(state, value) {
        state.isUnpacking = value;
    },

    SET_PAGE_TITLE(state, title) {
        state.pageTitle = title;
    },

    // SOCKET HEALTH
    SET_SOCKET_STATE(state, value) {
        state.socketState = value;
    },
    SET_ERROR(state, value) {
        state.error = value;
    },

    ADD_READY(state, room) {
        state.ready.push(room);
    },

    // OBSOLETE?
    SET_BYID(state, indexed) {
        state.byId = indexed;
    },
    ADD_ROOM_STATUS(state, room, msg) {
        if (!(room in state.statusesByRoom)) {
            Vue.set(state.statusesByRoom, room, []);
        }
        state.statusesByRoom.room.push(msg);
    },
    /*
    ADD_POINTER(state, item) {
        const index = state.pointers.findIndex(pointer => pointer.id === item.id);
        if (index !== -1) {
            Vue.set(state.pointers, index, item);
        } else {
            state.pointers.push(item);
        }
    },
    */
};

export const actions = {
    async connectAndJoin(context, {me, room}) {
        console.log("connectAndJoin:", room, me);
        // If the socket already exists and is active, just trigger a chat.join
        if (_socket) {
            console.log("Using existing socket");
            if (room) {
                context.dispatch("sendJoin", room);
            }
            return true
        } else {
            context.commit('SET_SOCKET_STATE', WebSocket.CONNECTING);
            if (room) {
                context.dispatch("sendJoin", room);
            }
        }

        _socket = new WebSocket(
            'wss://'
            + window.location.host
            + EventService.urlForChatSocket()
        );
        _socket.onerror = (e) => {
            context.commit('SET_ERROR', e);
            // Errors don't change the socketState
            console.error('Chat socket error:', e);
        };
        _socket.onclose = (e) => {
            context.commit('SET_SOCKET_STATE', WebSocket.CLOSED);
            console.log('Chat socket closed', e);

            // Attempt to reconnect, up to _MAX times
            if (_attempts <= _MAX) {
                setTimeout(() => {
                    _attempts++;
                    // console.log("Reconnection attempt:", _attempts);
                    _socket = null; // Wipe the existing socket so that we force a fresh connnection
                    // TODO: Re-enable the join
                    context.dispatch('connectAndJoin', {me, room});
                    
                }, _attempts*5000);
            }
        };
        _socket.onmessage = (e) => {
            const data = JSON.parse(e.data);
            // console.log("connectAndJoin:onmessage" + me.full_name);

            if (Array.isArray(data)) {
                // Catch any compressed multi-message objects
                context.commit('SET_UNPACKING', true);
                data.forEach( (msg, index, arr) => { 
                    if (Object.is(arr.length -1, index)) {
                        // Turn off the unpacking flag before the last message in the set
                        console.log("Last object");
                        context.commit('SET_UNPACKING', false);        
                    }
                    context.dispatch("handleMessageFromSocket", {msg, me});
                });
                context.commit('SET_UNPACKING', false); // Ensure it's off
            } else {
                // Standard action
                context.dispatch("handleMessageFromSocket", {msg: data, me: me});
            }

            context.commit('UPDATE_UNREAD_BY_ROOM');
        };
        _socket.onopen = () => {
            _attempts = 1; // Reset the connection attempt count on success
            context.commit('SET_SOCKET_STATE', WebSocket.OPEN);

            context.dispatch('sendOnline');
            while(_queue.length) {
                // console.log("Emptying queue");
                _socket.send(_queue.shift());
            }
        };

        // ----------------------
        // Faux Bootstrap Actions
        // console.log("Page title currently:", context.state.pageTitle);
        // if (!context.state.pageTitle) {
        //     console.log("Setting page title to:", document.title);
        //     context.commit('SET_PAGE_TITLE', document.title);
        // }
    },
    //-SEND* ACTIONS
    //-MEMBERSHIP
    sendJoin(context, room) {
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.JOIN,
            'room': room,
        }));
    },
    // sendLeave
    sendLeave(context, room) {
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.LEAVE,
            'room': room,
        }));
    },
    sendPresence(context, room) {
        // Request the list of who's been/is in the room
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.PRESENCE,
            'room': room,
        }));
    },
    sendOnline() {
        // Request the initial list of who's online
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.ONLINE,
        }));
    },
    //-ATTENDEE
    sendMessage(context, {room, message}) {
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.MESSAGE,
            'room': room,
            'message': message
        }));
    },
    sendReadPointer(context, {room, messageId}) {
        sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.READ,
            'room': room,
            'message_id': messageId
        }));
        context.commit('SET_MY_POINTERS_BY_ROOM', {room: room, message_id: messageId});
        context.commit('UPDATE_UNREAD_BY_ROOM');
    },
    // sendHistory
    sendHistory(context, room) {
        // Request a page of messages for this room
        let oldest = null
        let sendMsg = {
            'type': MSG_TYPES.HISTORY,
            'room': room,
        }
        let messages = context.getters.byRoom(room);
        if (messages) {
            // Ascending sort
            oldest = messages.sort((a,b) => a.message_id - b.message_id).shift()
        }
        if (oldest) {
            sendMsg['before'] = oldest.message_id;
        }
        sendOrQueue(JSON.stringify(sendMsg));
    },
    //-MODERATION
    sendDelete(context, {room, messageId}) {
        if (messageId) {
          sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.DELETE,
            'room': room,
            'message_id': messageId
          }));
        }
      },
    sendDeleteAll(context, {room}) {
        sendOrQueue(JSON.stringify({
          'type':  MSG_TYPES.DELETE_ALL,
          'room': room,
        }));
    },
    sendBan(context, {room, attendeeId}) {
        console.log("BAN:", attendeeId);
        if (attendeeId) {
          sendOrQueue(JSON.stringify({
            'type': MSG_TYPES.BAN,
            'room': room,
            'attendee_id': attendeeId
          }));
        }
    },
    

    // UI-friendly logic
    //
    /* Cache draft message text so that users can switch between rooms and not lose their drafts
     */
    updateDraftByRoom(context, {room, draft}) {
        context.commit('SET_DRAFT_BY_ROOM', {room, draft});
    },
    /* Delete a pending room that hasn't been messaged on yet.
     * e.g. you found an attendee, started messaging and then decided you didn't want to after all.
     *
     * Remove the room from state.latest
     * Remove the room from state.threadCache
     */
    removePendingRoom(context, room) {
        context.commit('REMOVE_FROM_LATEST', {room: room}); // Simulated 'latest' item with just a room field
        context.commit('REMOVE_FROM_THREAD_CACHE', {room: room}); // Another simulated item ;-)
        context.dispatch('updateDraftByRoom', {room: room, draft: ''});
    },


    // MESSAGE ROUTING
    handleMessageFromSocket(context, {msg, me}) {
        // Receive a message on the shared socket and route it correctly
        switch (msg.type) {
            case MSG_TYPES.MESSAGE:
                context.dispatch('receivedChatMessage', {msg, me});

                if (!context.state.isUnpacking) {
                    let pointer = context.state.myPointersByRoom[msg.room];
                    if (!pointer || pointer < msg.message_id) {
                        // This is a NEW message
                        if (msg.sender_id == me.id) {
                            context.dispatch('sendReadPointer', {room: msg.room, messageId: msg.message_id});
                        } else {
                            if (!muteAlerts()) {
                                // console.log("Notification played");
                                audioNotification.play();
                            }
                        }
                        
                    }
                    // context.dispatch('UPDATE_UNREAD_BY_ROOM');
                }
                
                /*
                if (!context.state.isUnpacking && (msg.sender_id != me.id) && !muteAlerts()) {
                    // console.log("Notification played");
                    audioNotification.play();
                }
                if (!context.state.isUnpacking && (msg.sender_id == me.id)) {
                    // Receiving a message I sent, so we can assume I've read it.
                    // TODO: DO WE NEED TO SEND A MESSAGE BACK TO THE SERVER OR IS THAT WASTED EFFORT?
                    context.dispatch('sendReadPointer', {room: msg.room, messageId: msg.message_id});
                }
                */
                break;
            case MSG_TYPES.JOIN:
                if (msg.attendee_id == me.id) {
                    // Once we're receive a chat.join response we can start using the room.
                    context.dispatch("sendHistory", msg.room);
                    context.dispatch("sendPresence", msg.room);
                    context.commit("ADD_READY", msg.room);
                } else {
                    // Someone else has joined the chatroom. Add their presence data.
                    context.commit('ADD_PRESENCES', {room: msg.room, messages: [msg]});
                }
                break;
            case MSG_TYPES.LEAVE:
                break;
            case MSG_TYPES.PRESENCE:
                context.commit('ADD_PRESENCES', {room: msg.room, messages: msg.data});
                break;
            case MSG_TYPES.HISTORY:
                // History returns a page-worth of messages sent as a list called 'messages'
                context.commit('SET_UNPACKING', true);
                if (msg.messages) {
                    msg.messages.forEach(msg => {
                        context.dispatch("handleMessageFromSocket", {msg, me});
                    });
                }
                if (msg.messages.length < HISTORY_PAGE_SIZE) {
                    // If the list is empty or we don't have a full page of results, we've reached the start of the message thread.
                    let roomMessages = context.getters.byRoom(msg.room);
                    let firstMsg = roomMessages[0];
                    if (firstMsg) {
                        context.commit('SET_FIRST_BY_ROOM', {room: msg.room, message_id: firstMsg.message_id});
                    }
                }
                context.commit('SET_UNPACKING', false);
                break;
            case MSG_TYPES.DELETE:
                context.commit('REMOVE_MESSAGE_BY_ID', msg.message_id);
                break;
            case MSG_TYPES.DELETE_ALL:
                // Loop through all the messages in the room and remove them
                context.commit('REMOVE_MESSAGES_BY_ROOM', msg.room);
                break;    
            case MSG_TYPES.BAN:
                // Someone has been banned. Might be me, might be someone else...
                if (msg.attendee_id == me.id) {
                    console.log("I have been banned!");
                    context.commit('REMOVE_PRESENCE', {room: msg.room, attendee_id: msg.attendee_id});
                    context.commit('REMOVE_FROM_LATEST', msg);
                    context.commit('REMOVE_MESSAGES_BY_ROOM', msg.room);

                    // Bounce the banned attendee back to the index
                    // TODO: Add a message
                    MessageRouter.replace({name: 'message-index'});
                    // location.reload(); // PHP should trigger a redirect action+banned message
                } else {
                    context.commit('REMOVE_PRESENCE', {room: msg.room, attendee_id: msg.attendee_id});
                }
                break;
            case MSG_TYPES.ONLINE:
                // List of ALL the online attendee_ids
                // Or differential connected/disconnected updates
                if (msg.connected) {
                    context.commit('ADD_ONLINE', msg.connected);    
                }
                if (msg.disconnected) {
                    context.commit('REMOVE_ONLINE', msg.disconnected);    
                }
                break;
            case MSG_TYPES.ERROR_CHAT:
                context.commit('SET_ERROR_BY_ROOM', msg);
                break;
        }
    },

    receivedChatMessage(context, {msg, me}) {
        // Add the message to the message store
        context.commit("INSERT_MESSAGE", msg);

        // Now see if we need to update state.latest[] sidebar area
        const found = context.state.latest.find(lMsg => lMsg.room == msg.room);
        if (found) {
            if (msg.message_id > found.message_id || !found.message_id) {  // Pending chats don't have an id
                context.commit('REMOVE_FROM_LATEST', found);
                context.commit('PREPEND_NEW', msg);
            }
        } else {
            // This must be a new inbound thread, so try to create a corresponding thread
            // context.commit('SET_THREAD_CACHE', [convertMessageToThread(msg, me)]);
            context.dispatch('updateThreadCache', [convertMessageToThread(msg, me)]);

            // New message goes straight to the top of the list
            context.commit('PREPEND_NEW', msg);
        }
    },

    /** Thread Cache data includes who's messaging who */
    updateThreadCache(context, threads) {
        context.commit('SET_THREAD_CACHE', threads);

        // Track who's messaging/meeting who.
        threads.forEach(thread => {
            if (thread.meeting) {
                // Meeting thread, so snag the booker and attendee
                // TODO: Colleagues?
                context.commit('SET_ROOM_BY_ATTENDEE', {room: thread.room, attendee_id: thread.meeting.booker_id});
                context.commit('SET_ROOM_BY_ATTENDEE', {room: thread.room, attendee_id: thread.meeting.attendee_id});
            // } else if (thread.room.indexOf('-at-') > 0) {
            } else if (thread.attendee_id) {
                // Attendee thread
                context.commit('SET_ROOM_BY_ATTENDEE', {room: thread.room, attendee_id: thread.attendee_id});
                context.commit('SET_ROOM_BY_ATTENDEE', {room: thread.room, attendee_id: thread.sender_id});
            }
        });
    },

    /*
    async fetchPointers(context) {
        return EventService.getMessagePointers()
            .then(response => {
                // Massage attendee_pointers into standard format
                console.log("fetchPointers", response);
                console.log(response.data)
                response.data['pointers'].forEach(row => {
                    context.commit('ADD_POINTER', row);
                })
            })
            .catch(error => {
               console.log('getMessagePointers failed: ', error.message)
            });
    },

    addPointers(context, pointers) {
        console.log("ACTIONS:addPointers", pointers);
        pointers.forEach(row => {
            context.commit('ADD_POINTER', row);
        });
    },
    */
    startThread(context, {threadId, sender, attendee}) {
        // Prepend a simulated thread to state.latest
        const newThread = {
            attendee: attendee,
            attendee_id: attendee.id,
            room: threadId,
            sender: sender,
            sender_id: sender.id,
        };
        // context.commit('SET_THREAD_CACHE', [newThread]);
        context.dispatch('updateThreadCache', [newThread]);
        context.commit('PREPEND_NEW', convertThreadToMessage(newThread));
    },

    async fetchLatest(context) {
        return EventService.getLatestMessages()
            .then(response => {
                // context.commit('SET_THREAD_CACHE', response.data);
                context.dispatch('updateThreadCache', response.data);
                const data = response.data.map(convertThreadToMessage);
                context.commit('SET_LATEST', data);
                data.forEach(msg => {
                    // Add each latest message to the message index (after conversion from thread->chat)
                    context.commit('INSERT_MESSAGE', msg);
                });
            })
            .catch(error => {
               console.log('getLatest failed: ', error.message);
            });
    },
    async fetchMyPointers(context) {
        return EventService.getMyMessagePointers()
            .then(response => {
                console.log("Message Pointers:", response.data);
                if ('pointers' in response.data && response.data.pointers) {
                    response.data.pointers.forEach(pointer => {
                        context.commit('SET_MY_POINTERS_BY_ROOM', {room: pointer.slug, message_id: pointer.last_message_id});
                    });
                }
            });
    },
    async fetchMessageForRoom(context, room) {
        return EventService.getLatestMessageForRoom(room)
            .then(response => {
                console.log("Latest message for room:", response.data);
                console.log("Converted", convertThreadToMessage(response.data));
                // context.commit('SET_THREAD_CACHE', [response.data]);
                context.dispatch('updateThreadCache', [response.data]);
                context.commit('PREPEND_NEW', convertThreadToMessage(response.data));
            })
            .catch(error => {
                console.log('getLatestMessageForRoom failed: ', error.message);
            });
    },


    backgroundSocket({dispatch}) {
        if (EventService.hasValidAuthToken()) {
            bgSocket = new WebSocket(
                'wss://'
                + window.location.host
                + EventService.urlForLatestMessagesChatSocket()
            );

            bgSocket.onmessage = (e) => {
                const data = JSON.parse(e.data);
                console.log(data);

                if (data.subtype === CHAT_SUBTYPES.MESSAGE) {
                    dispatch('newMessage', data);
                }
            };

            bgSocket.onerror = (e) => {
                console.error('LatestMessage Chat socket error:', e);
            };

            bgSocket.onclose = (e) => {
                console.log('LatestMessage Chat socket closed', e);
            };
        }
    }
};

/*
const pointersToDict = (pointerList) => {
    let indexed = {};
    console.log("pointersToDict:", pointerList)
    if (pointerList) {
        pointerList.forEach(pointer => {
            indexed[pointer.slug] = pointer.last_message_id;
        });
    }
    return indexed;
}
*/

export const getters = {
    allLatest(state) {
        return state.latest;
    },
    // Get all the messages for the room specified
    byRoom: (state) => (room) => {
        return state.messages.filter(msg => msg.room == room);
    },
    isReady: (state) => (room) => {
        return state.ready.includes(room);
    },
    isHistoryComplete: (state) => (room) => {
        return room in state.firstByRoom;
    },
    threadById: (state) => (threadId) => {
        return state.latest.find(thread => thread.room == threadId);
    },
    // Get all the presences for the room specified [{attendee_id, name, organisation_name, timestamp},...]
    presencesByRoom: (state) => (room) => {
        if (room in state.presencesByRoom) {
            return state.presencesByRoom[room];
        } else {
            return [];
        }
    },
    roomsByAttendeeId: (state) => (attendee_id) => {
        if (attendee_id in state.roomsByAttendeeId) {
            return state.roomsByAttendeeId[attendee_id];
        } else {
            return [];
        }
    },
    isAttendeeOnline: (state) => (attendeeId) => {
        return state.online.includes(attendeeId);
    },
    socketState(state) {
        return state.socketState;
    },
    socketError(state) {
        return state.error;
    },
    threadCache: (state) => (threadId) => {
        return state.threadCache[threadId];
    },
    myPointers(state) {
        return state.myPointersByRoom;
    },
    errorByRoom: (state) => (room) => {
        return state.errorByRoom[room];
    },
    draftByRoom: (state) => (room) => {
        return state.draftByRoom[room] || '';
    },

    /* Object/dict of rooms with the id of the last message read */
    unreadRooms(state) {
        return state.unreadByRoom;
        /*
        let unread = {};
        state.latest.forEach(msg => {
            if (msg.room in state.myPointersByRoom) {
                if (msg.id > state.myPointersByRoom[msg.room]) {
                    unread[msg.room] = state.myPointersByRoom[msg.room];
                }
            } else {
                // No pointer, so it must be unread
                unread[msg.room] = 0;
            }
        });

        // let unreads = Object.keys(unread).length
        // if (unreads) {
        //     document.title = `(${unreads}) ` + state.pageTitle;
        // }
        return unread;
        */
    },
    countUnreadRooms(state) {
        return Object.keys(state.unreadByRoom).length;
        // let unread = getters.unreadRooms;
        // return Object.keys(unread).length;
    },
    /* GET A COUNT OF THE UNREAD MESSAGES SO THAT WE CAN SHOW (1) etc.
     * Messages list may be incomplete, so pointer is unreliable
     * Return int or string :-/
     */
    countUnreadMessagesByRoom: (state, getters) => (room) => {
        let messages = getters.byRoom(room);
        if (! (room in state.myPointersByRoom)) {
            // If we don't have a pointer, we're at least messages.length out of date, hence the "+"
            if (messages.length > 0) {
                return messages.length +"+"; 
            }
            // Empty messages list, so may be a new chat
            return 0;
        }
        let pointer = state.myPointersByRoom[room];
        let pointer_index = messages.findIndex(msg => msg.message_id === pointer);
        return messages.length - 1 - pointer_index;
    }

    /*
    pointersForAttendeeId: (state) => (attendeeId) => {
        return state.pointers.filter(pointer => pointer.attendee_id === attendeeId);
    },
    pointersForThread: (state) => (thread) => {
        return state.pointers.filter(pointer => pointer.slug === thread);
    },
    freshThreadIds: (state, getters) => (attendeeId) => {
        let forAttendee = pointersToDict(getters.pointersForAttendeeId(attendeeId));
        let fresh = []; // threadIds where this attendee is not up to date
        state.latest.forEach(msg => {
            if (msg.thread in forAttendee) {
                if (msg.id > forAttendee[msg.thread]) {
                    fresh.push(msg.thread);
                }
            } else {
                // No pointer, so it must be unread
                fresh.push(msg.thread);
            }
        });

        return fresh;
    }
    */
};
